import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { PageHeader } from "../../components/admin/PageHeader";
import { DataTable, Pagination } from "../../components/admin/DataTable";
import { ExportButtons } from "../../components/admin/ExportButtons";
import { ConfirmDialog } from "../../components/admin/ConfirmDialog";
import {
  emptyDraftFilters, fetchDrafts, fetchAllDrafts, setDraftFollowup, followupLabel, followupBadge, FOLLOWUP_STATUSES, DRAFT_EXPORT_COLUMNS,
} from "../../lib/draftApi";
import { fmtDate, fmtDateTime } from "../../lib/adminApi";

const PAGE_SIZE = 20;
const Txt = ({ label, value, onChange, testId, ...p }) => (
  <label className="space-y-1 text-xs font-medium text-[#8C6A6C]">{label}<Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="rmm-input h-9 bg-white text-[#1A1515]" {...p} /></label>
);
const Sel = ({ label, value, onChange, options, testId }) => (
  <label className="space-y-1 text-xs font-medium text-[#8C6A6C]">{label}
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="rmm-select rmm-input h-9 text-[#1A1515]">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export default function IncompleteRegistrations() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(emptyDraftFilters);
  const [debounced, setDebounced] = useState(emptyDraftFilters);
  const [sort, setSort] = useState({ key: "last_activity_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(filters); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchDrafts({ filters: debounced, sort, page, pageSize: PAGE_SIZE })); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [debounced, sort, page]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const columns = [
    { key: "full_name", label: "Name", render: (r) => <span className="font-semibold text-[#7A0016]">{r.full_name || "Not provided"}</span> },
    { key: "email", label: "Signup email", sortable: true },
    { key: "signup_method", label: "Method", render: (r) => <span className="capitalize">{r.signup_method === "google" ? "Google" : "Email"}</span> },
    { key: "applicant_mobile", label: "Applicant mobile", render: (r) => r.applicant_mobile || "Not provided" },
    { key: "phones", label: "Phone / WhatsApp", render: (r) => [r.present_phone, r.present_whatsapp].filter(Boolean).join(" / ") || "-" },
    { key: "profile_for", label: "For", render: (r) => (r.profile_for ? <span className="capitalize">{r.profile_for}</span> : "-") },
    { key: "current_step", label: "Last step", sortable: true, render: (r) => `Step ${r.current_step} of 8` },
    { key: "pct", label: "% done", render: (r) => `${Math.round((r.current_step / 8) * 100)}%` },
    { key: "signed_up_at", label: "Signed up", sortable: true, render: (r) => fmtDate(r.signed_up_at) },
    { key: "last_activity_at", label: "Last activity", sortable: true, render: (r) => fmtDateTime(r.last_activity_at) },
    { key: "follow_up_status", label: "Follow-up", render: (r) => <span data-testid={`followup-${r.user_id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${followupBadge(r.follow_up_status)}`}>{followupLabel(r.follow_up_status)}</span> },
  ];

  const startBulk = () => {
    const ids = [...selected];
    setBulkAction({
      title: `Set follow-up status for ${ids.length} registration(s)`,
      description: "Pick a status. An optional note is saved with the date and your name.",
      confirmLabel: "Save status",
      custom: true,
      run: async ({ status, note }) => {
        await Promise.all(ids.map((id) => setDraftFollowup(id, status, note)));
        toast.success(`Follow-up status set for ${ids.length} registration(s)`);
        setSelected(new Set());
        load();
      },
    });
  };

  return (
    <div data-testid="incomplete-registrations-page">
      <PageHeader eyebrow="User Management" title="Incomplete Registrations" />
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8C6A6C]" />
            <Input data-testid="draft-search-input" placeholder="Search name, email or phone" value={filters.q} onChange={(e) => set("q")(e.target.value)} className="rmm-input h-10 bg-white pl-9" />
          </div>
          <button type="button" onClick={() => setFilters(emptyDraftFilters)} data-testid="draft-reset-button" className="inline-flex items-center gap-1.5 rounded-full px-3 text-sm text-[#8C6A6C] hover:text-[#7A0016]"><RotateCcw className="h-4 w-4" /> Reset</button>
        </div>
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-[#D4AF37]/30 bg-white/80 p-4 sm:grid-cols-3 lg:grid-cols-6">
          <Sel label="Last step reached" value={filters.step} onChange={set("step")} options={[["", "Any"], ...[1, 2, 3, 4, 5, 6, 7].map((n) => [String(n), `Step ${n}`])]} testId="draft-filter-step" />
          <Sel label="Bride / Groom" value={filters.profileFor} onChange={set("profileFor")} options={[["", "All"], ["bride", "Bride"], ["groom", "Groom"]]} testId="draft-filter-for" />
          <Sel label="Follow-up status" value={filters.followUp} onChange={set("followUp")} options={[["", "All"], ...FOLLOWUP_STATUSES.map((s) => [s.value, s.label])]} testId="draft-filter-followup" />
          <Txt label="Signed up from" type="date" value={filters.from} onChange={set("from")} testId="draft-filter-from" />
          <Txt label="Signed up to" type="date" value={filters.to} onChange={set("to")} testId="draft-filter-to" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div data-testid="draft-bulk-actions">
            {selected.size > 0 && (
              <span className="inline-flex items-center gap-2">
                <span className="text-xs font-semibold text-[#7A0016]">{selected.size} selected</span>
                <button type="button" onClick={startBulk} data-testid="bulk-followup-button" className="rounded-full bg-[#7A0016] px-3 py-1.5 text-xs font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">Set follow-up status</button>
              </span>
            )}
          </div>
          <ExportButtons fetchRows={() => fetchAllDrafts(debounced, sort)} columns={DRAFT_EXPORT_COLUMNS} name="incomplete-registrations" title="Real Match Matrimony - Incomplete Registrations" />
        </div>
        <DataTable columns={columns} rows={data.rows.map((r) => ({ ...r, id: r.user_id }))} loading={loading} selectable selected={selected} onSelect={setSelected} sort={sort} onSort={setSort} onRow={(r) => navigate(`/admin/incomplete/${r.user_id}`)} />
        <Pagination page={page} pageSize={PAGE_SIZE} count={data.count} onPage={setPage} />
      </div>
      <FollowupDialog action={bulkAction} onClose={() => setBulkAction(null)} />
    </div>
  );
}

function FollowupDialog({ action, onClose }) {
  const [status, setStatus] = useState("contacted");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStatus("contacted"); setNote(""); }, [action]);
  if (!action) return null;
  return (
    <ConfirmDialog
      action={{
        ...action,
        custom: true,
        run: async () => {
          setBusy(true);
          try { await action.run({ status, note: note.trim() }); onClose(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
        },
      }}
      onClose={onClose}
      extra={
        <div className="space-y-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="followup-status-select" className="rmm-select rmm-input h-10 w-full">
            {FOLLOWUP_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Note (optional, saved with date and your name)" data-testid="followup-note-input" className="rmm-input w-full rounded-md border border-input bg-white p-3 text-sm" />
        </div>
      }
    />
  );
}

export function FollowupDialogWrapper() { return null; }
void Loader2;
